import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import api from '../../services/api';
import { useWorkspace } from '../../utils/WorkspaceContext';
import useDebounce from '../../hooks/debounceHook';
import TrackDepartmentSelects from '../KeyComponents/TrackDepartmentSelects';

/**
 * @typedef {Object} TrackFormData
 * @property {string} name - Track name
 * @property {string} description - Track description
 * @property {string} project - Project ID
 * @property {string} assignee - Assignee user ID
 * @property {?number} department - Default department for the track's tasks
 * @property {?number} division - Default division for the track's tasks
 * @property {number} [workspace] - Workspace ID (added on submit)
 */

/**
 * @typedef {Object} Project
 * @property {number} id - Project ID
 * @property {string} name - Project name
 */

/**
 * @typedef {Object} Assignee
 * @property {number} id - Assignee ID
 * @property {string} full_name - Assignee's full name
 */

/**
 * @typedef {Object} CreateTrackDialogProps
 * @property {string} [projectId] - Pre-selected project ID
 * @property {boolean} open - Whether the dialog is open
 * @property {function} onClose - Function to call when dialog should close
 * @property {function} onSuccess - Function to call when track is created successfully
 */

/**
 * Dialog component for creating new tracks
 * @component
 * @param {CreateTrackDialogProps} props - Component props
 * @returns {React.ReactElement} The create track dialog
 */
const CreateTrackDialog = ({ projectId, open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    project: projectId || '',
    assignee: '',
    department: null,
    division: null,
  });
  const [projects, setProjects] = useState([]);
  const [assignees, setAssignees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const { currentWorkspace } = useWorkspace();

  /**
   * Debounced function to create a track
   * @param {TrackFormData} trackData - The track data to submit
   */
  const debouncedCreateTrack = useDebounce(async (trackData) => {
    try {
      await api.post('tracks/', trackData);
      onSuccess();
    } catch (error) {
      console.error('Error creating track:', error);
    }
  });

  useEffect(() => {
    /**
     * Fetches available projects from the API
     * @async
     * @returns {Promise<void>}
     */
    const fetchProjects = async () => {
      try {
        const response = await api.get('projects/', {
          params: { workspace_id: currentWorkspace.id },
        });
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    /**
     * Fetches available assignees from the API
     * @async
     * @returns {Promise<void>}
     */
    const fetchAssignees = async () => {
      try {
        const response = await api.get('assignees/', {params: { workspace_id: currentWorkspace.id }});
        setAssignees(response.data);
      } catch (error) {
        console.error('Error fetching assignees:', error);
      }
    };

    const fetchDepartmentsAndDivisions = async () => {
      try {
        const params = { workspace_id: currentWorkspace.id };
        const [departmentsRes, divisionsRes] = await Promise.all([
          api.get('departments/', { params }),
          api.get('divisions/', { params }),
        ]);
        setDepartments(departmentsRes.data);
        setDivisions(divisionsRes.data);
      } catch (error) {
        console.error('Error fetching departments/divisions:', error);
      }
    };

    fetchProjects();
    fetchAssignees();
    fetchDepartmentsAndDivisions();
  }, [currentWorkspace.id]);

  /**
   * Handles form submission
   * @async
   * @param {React.FormEvent<HTMLFormElement>} e - Form submit event
   * @returns {Promise<void>}
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.project === '') {
      formData.project = projectId;
    }
    formData.workspace = currentWorkspace.id;
    debouncedCreateTrack(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create New Track</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Track Name"
            fullWidth
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={4}
            value={formData.description}
            onChange={(e) =>
              setFormData({ ...formData, description: e.target.value })
            }
          />
          <TextField
            select
            margin="dense"
            label="Project"
            fullWidth
            required
            value={formData.project}
            onChange={(e) =>
              setFormData({ ...formData, project: e.target.value })
            }
          >
            {projects.map((project) => (
              <MenuItem key={project.id} value={project.id}>
                {project.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Assignee"
            fullWidth
            value={formData.assignee}
            onChange={(e) =>
              setFormData({ ...formData, assignee: e.target.value })
            }
          >
            <MenuItem value="">None</MenuItem>
            {assignees.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.full_name}
              </MenuItem>
            ))}
          </TextField>
          <TrackDepartmentSelects
            departments={departments}
            divisions={divisions}
            value={formData}
            onChange={(updates) => setFormData({ ...formData, ...updates })}
            fieldProps={{ margin: 'dense', fullWidth: true }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained">
            Create
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateTrackDialog;
